/** Global Vistoera Brand Entry — Canvas SaaS Shell overlay
 * (VISTOERA_CANVAS_MY_VISTOERA_ENTRY_AND_VIEWPORT_CONTROLS_REPOSITION_P1_V1,
 * VISTOERA_TENANT_OVERVIEW_DASHBOARD_LAYOUT_AND_CANVAS_RETURN_CONTEXT_P1_V1).
 *
 * Fixed at the top of the left capability rail. Brand mark is the
 * canonical static Vistoera V logo asset; click navigates in the CURRENT tab to the tenant
 * Overview surface for this workspace (not browser history back or Home
 * Canvas). Return route is derived from the Canvas URL's `projectId`. */
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ensureActiveCanvasReturnContext } from '../../../navigation/canvasNavigationContext'
import { useCanvasWorkspaceTransition, exitCanvasToBackoffice } from '../../canvasWorkspaceTransition'
import {
  resolveTenantBackofficeReturnLocation,
  tenantBackofficeReturnPath,
} from '../../tenantBackofficeReturnLocation'
import VistoeraVLogo from '../../../../components/VistoeraVLogo'

interface Props {
  workspaceId: string
  contentProjectId: string
}

/** Canvas → Tenant Backoffice canonical exit trigger
 * (VISTOERA_WORKSPACE_CANVAS_BIDIRECTIONAL_SPATIAL_SHELL_TRANSITION_AND_VIEWPORT_CONTINUITY_P0_V1
 * §11/§30/§31/§66,
 * VISTOERA_CANVAS_TO_TENANT_BACKOFFICE_RETURN_STATE_RESTORE_SKELETON_FLASH_ELIMINATION_AND_LAST_BACKOFFICE_LOCATION_MEMORY_P0_V1
 * §118-§121, VistoeraTASK-00552). The destination is
 * `resolveTenantBackofficeReturnLocation` — the last healthy Tenant
 * Backoffice page the user was on before entering Canvas (route/query/scroll),
 * never a hardcoded Overview URL (spec §120).
 *
 * At click time we also re-assert Canvas Return Context via
 * `ensureActiveCanvasReturnContext` (same helper the account-menu exit
 * already uses). Mount-time sync on `ProductionCanvasPage` remains the
 * refresh-safe baseline; the exit-time write closes gaps when
 * sessionStorage alone is flaky.
 *
 * The transition experience is unchanged: the real `navigate()` still
 * runs through `requestExitToWorkspace`, which slides the whole Canvas
 * Surface off-screen right first (`CanvasTransitionSurface`) and guards
 * against rapid repeat clicks (at most one navigation per gesture —
 * spec §66/§161 "V V V V → NAVIGATION_COUNT=1"). The resolved scroll
 * position (if any) rides along as one-shot router `state`, consumed
 * exactly once by `WorkspaceLayout.tsx`'s restore effect — never a
 * second `navigate()` call. */
export default function CanvasBrandEntry({ workspaceId, contentProjectId }: Props) {
  const navigate = useNavigate()
  const { t } = useTranslation('canvas')
  const { phase, requestExitToWorkspace } = useCanvasWorkspaceTransition()

  const onClick = () => {
    ensureActiveCanvasReturnContext(workspaceId, contentProjectId, 'direct-url')
    const resolved = resolveTenantBackofficeReturnLocation(workspaceId)
    const destination = tenantBackofficeReturnPath(resolved)
    exitCanvasToBackoffice(
      requestExitToWorkspace,
      navigate,
      destination,
      resolved.scrollY > 0 ? { scrollY: resolved.scrollY } : undefined,
    )
  }

  return (
    <div className="pcv-brand-entry">
      <Tooltip title={t('chrome.brandEntry.tooltip')} placement="right" mouseEnterDelay={0.35}>
        <button
          type="button"
          className="pcv-brand-entry-btn"
          onClick={onClick}
          disabled={phase !== 'idle'}
          aria-label={t('chrome.brandEntry.ariaLabel')}
        >
          <VistoeraVLogo className="pcv-brand-entry-mark" />
        </button>
      </Tooltip>
    </div>
  )
}
